import { Routes } from '@angular/router';
import { authCanMatch, contextReadyCanActivate, featureFlagCanMatch, permissionGuard, CallbackComponent, LogoutComponent, NotificationSetupComponent} from '@lr/core';
import { AccessDeniedComponent, NotFoundComponent } from '@lr/ui';
import { hasGroupCanActivate } from './guards/has-group.guard';
import { documentViewCanActivate } from './guards/document-view.guard';

export const routes: Routes = [
  {
    path: 'callback',
    component: CallbackComponent,
    title: 'Signing in…'
  },
  {
    path: 'login',
    loadComponent: () => import('./pages/login/login.component').then(m => m.LoginComponent),
    canMatch: [authCanMatch],
    title: 'Login'
  },
  {
    path: 'logout',
    component: LogoutComponent,
    title: 'Logout'
  },
  {
    path: 'client',
    redirectTo: 'dashboard',
    pathMatch: 'full'
  },
  {
    path: 'client-selection',
    loadComponent: () =>
      import('./pages/client-selection/client-selection.component').then(
        (m) => m.ClientSelectionComponent
      ),
    canMatch: [authCanMatch],
    canActivate: [contextReadyCanActivate, hasGroupCanActivate],
    title: 'Select Group'
  },
  {
    path: 'notification-setup',
    component: NotificationSetupComponent,
    canMatch: [authCanMatch],
    canActivate: [contextReadyCanActivate, hasGroupCanActivate],
    title: 'Notification Setup'
  },
  {
    path: '',
    redirectTo: 'dashboard',
    pathMatch: 'full'
  },
  {
    path: 'dashboard',
    loadComponent: () => import('./pages/dashboard/dashboard.component').then(m => m.DashboardComponent),
    canMatch: [authCanMatch],
    canActivate: [contextReadyCanActivate, hasGroupCanActivate],
    title: 'Dashboard'
  },
  {
    path: 'users',
    loadComponent: () => import('./pages/user-management/user-management.component').then(m => m.UserManagementComponent),
    canMatch: [authCanMatch],
    canActivate: [contextReadyCanActivate, hasGroupCanActivate, permissionGuard('Users.View')],
    title: 'User Management'
  },
  {
    path: 'users/new',
    loadComponent: () => import('./pages/user-management/user-form/user-form.component').then(m => m.UserFormComponent),
    canMatch: [authCanMatch],
    canActivate: [contextReadyCanActivate, hasGroupCanActivate, permissionGuard('Users.Manage')],
    title: 'Add New User'
  },
  {
    path: 'users/:id/edit',
    loadComponent: () => import('./pages/user-management/user-form/user-form.component').then(m => m.UserFormComponent),
    canMatch: [authCanMatch],
    canActivate: [contextReadyCanActivate, hasGroupCanActivate, permissionGuard('Users.Manage')],
    title: 'Edit User'
  },
  {
    path: 'account/subgroups',
    loadComponent: () => import('./pages/account/subgroups/subgroups.component').then(m => m.SubgroupsComponent),
    canMatch: [authCanMatch],
    canActivate: [contextReadyCanActivate, hasGroupCanActivate],
    title: 'Companies'
  },
  {
    path: 'account/settings',
    loadComponent: () => import('./pages/settings/settings.component').then(m => m.SettingsComponent),
    canMatch: [authCanMatch],
    canActivate: [contextReadyCanActivate, hasGroupCanActivate],
    title: 'User Preferences'
  },
  {
    path: 'billing/invoices/:invoiceId',
    loadComponent: () =>
      import('./pages/invoices/invoice-detail/invoice-detail.component').then(m => m.InvoiceDetailComponent),
    canMatch: [authCanMatch],
    canActivate: [contextReadyCanActivate, hasGroupCanActivate, permissionGuard('Invoices.View')],
    title: 'Invoice'
  },
  {
    path: 'billing/invoices',
    loadComponent: () => import('./pages/invoices/invoices.component').then(m => m.InvoicesComponent),
    canMatch: [authCanMatch],
    canActivate: [contextReadyCanActivate, hasGroupCanActivate, permissionGuard('Invoices.View')],
    title: 'Invoices'
  },
  {
    path: 'billing/payment-report',
    loadComponent: () => import('./pages/payment-report/payment-report.component').then(m => m.PaymentReportComponent),
    canMatch: [authCanMatch],
    canActivate: [contextReadyCanActivate, hasGroupCanActivate, permissionGuard('PaymentReports.View')],
    title: 'Payment Report'
  },
  {
    path: 'account/documents',
    loadComponent: () => import('./pages/account/documents/documents.component').then(m => m.DocumentsComponent),
    canMatch: [authCanMatch, featureFlagCanMatch('ClientDocuments')],
    canActivate: [contextReadyCanActivate, hasGroupCanActivate, documentViewCanActivate],
    title: 'Documents & Marketing Resources'
  },
  {
    path: 'subclients/:mcode/:ccode/edit',
    loadComponent: () => import('./pages/subclient-edit/subclient-edit.component').then(m => m.SubclientEditComponent),
    canMatch: [authCanMatch],
    canActivate: [contextReadyCanActivate, hasGroupCanActivate, permissionGuard('Company.Manage')],
    title: 'Edit Company'
  },
  {
    path: 'members/send-invitations',
    loadComponent: () =>
      import('./pages/members/send-invitations-placeholder/send-invitations-placeholder.component').then(
        (m) => m.SendInvitationsPlaceholderComponent
      ),
    canMatch: [authCanMatch],
    canActivate: [contextReadyCanActivate, hasGroupCanActivate],
    title: 'Invite an employee to enroll'
  },
  {
    path: 'members/add-member',
    loadComponent: () =>
      import('./pages/members/add-member/add-member.component').then((m) => m.AddMemberComponent),
    canMatch: [authCanMatch],
    canActivate: [contextReadyCanActivate, hasGroupCanActivate, permissionGuard('Enrollment.Manage')],
    title: 'Add Member Manually'
  },
  {
    path: 'members/bulk-import',
    loadComponent: () =>
      import('./pages/members/bulk-import/bulk-member-import.component').then(
        (m) => m.BulkMemberImportComponent
      ),
    canMatch: [authCanMatch, featureFlagCanMatch('BulkUploadMembers')],
    canActivate: [contextReadyCanActivate, hasGroupCanActivate, permissionGuard('Enrollment.Manage')],
    title: 'Bulk Import Members'
  },
  {
    path: 'group/:mcode/subgroup/:ccode/members/:subId',
    loadComponent: () =>
      import('./pages/members/member-detail/member-detail.component').then((m) => m.MemberDetailComponent),
    canMatch: [authCanMatch],
    canActivate: [contextReadyCanActivate, hasGroupCanActivate, permissionGuard('Enrollment.View')],
    title: 'Member'
  },
  {
    path: 'members',
    loadComponent: () =>
      import('./pages/members/members-management/members-management.component').then(
        (m) => m.MembersManagementComponent
      ),
    canMatch: [authCanMatch],
    canActivate: [contextReadyCanActivate, hasGroupCanActivate, permissionGuard('Enrollment.View')],
    pathMatch: 'full',
    title: 'Members Management'
  },
  {
    path: 'enrollments',
    loadComponent: () =>
      import('./pages/enrollments/enrollment-queue/enrollment-queue.component').then(
        (m) => m.EnrollmentQueueComponent
      ),
    canMatch: [authCanMatch],
    canActivate: [contextReadyCanActivate, hasGroupCanActivate, permissionGuard('Enrollment.View')],
    pathMatch: 'full',
    title: 'Enrollment Queue'
  },
  {
    path: 'access-denied',
    component: AccessDeniedComponent,
    canMatch: [authCanMatch],
    title: '403 Forbidden'
  },
  {
    path: 'unauthorized',
    loadComponent: () => import('./pages/unauthorized/unauthorized.component').then(m => m.UnauthorizedComponent),
    canMatch: [authCanMatch],
    title: '401 Unauthorized'
  },
  {
    path: 'no-group-access',
    loadComponent: () => import('./pages/no-group-access/no-group-access.component').then(m => m.NoGroupAccessComponent),
    canMatch: [authCanMatch],
    title: 'No group assigned'
  },
  {
    path: 'dashboard',
    loadComponent: () => import('@lr/core').then(m => m.InitializingComponent)
  },
  {
    path: '**',
    component: NotFoundComponent,
    title: 'Page Not Found'
  }
];
